import type { Mutation, MutationObserverBaseResult, QueryKey } from '@tanstack/query-core';
import type { DefaultError } from '@tanstack/react-query';
import {
    QueryClientProvider,
    useMutation,
    useMutationState,
    useQueryClient,
} from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { createElement, useCallback, useMemo, useState } from 'react';

import type {
    CreateMutationOptions,
    CreateMutationReturn,
    MutationState,
    UseMutationOptions,
    UseMutationState,
} from './createMutationTypes';
import { getQueryClient } from '../queryClientSingleton';
import { KeyNamespaces, getKey } from '../utils/getKey';
import type { CacheKey } from '../utils/internalTypings';
import { getCreateMutationDefaultOptions } from './initMutation';

function isPending(status?: MutationObserverBaseResult['status']): boolean {
    return status === 'pending';
}

const getMutationKey = <PayloadArg>(
    options: {
        cacheKey?: CacheKey<PayloadArg>;
        name: string;
    },
    arg?: PayloadArg,
): QueryKey => getKey(KeyNamespaces.MUTATION, options, arg);

const NO_CACHE = 0;

/**
 * Builder for custom useMutation hook. Difference vs. official implementation is that we re-use
 * mutation observer, so we can share data between imperative and hook calls.
 * @param options mutation options
 */
const createUseMutation =
    <Returned, PayloadArg = undefined>(options: CreateMutationOptions<Returned, PayloadArg>) =>
    (
        defaultArg?: PayloadArg,
        hookOptions?: UseMutationOptions,
    ): UseMutationState<Returned, PayloadArg> => {
        const mutationKey = useMemo(
            () => getMutationKey(options, defaultArg),
            [JSON.stringify(defaultArg)],
        );

        const mutationOptions: UseMutationOptions = {
            ...getCreateMutationDefaultOptions(),
            ...hookOptions,
        };

        const {
            mutateAsync: mutate,
            data,
            error,
            status,
            reset,
        } = useMutation<Returned, DefaultError, PayloadArg>({
            mutationKey,
            mutationFn: options.payloadCreator,
            gcTime: options.cache === false ? NO_CACHE : Infinity,
            throwOnError: !!mutationOptions?.throwOnError,
        });

        const states = useMutationState({
            filters: {
                mutationKey,
                exact: true,
            },
        });

        const state = states.length && states[states.length - 1];

        const client = useQueryClient();

        const resetWrapper = useCallback(() => {
            client
                .getMutationCache()
                .findAll({
                    mutationKey,
                    exact: true,
                })
                .forEach((mutation) => {
                    client.getMutationCache().remove(mutation);
                });

            reset();
        }, [client, mutationKey, reset]);

        const handleMutate = useCallback(
            (override?: PayloadArg) => {
                return mutate((override ?? defaultArg) as PayloadArg);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [JSON.stringify(defaultArg), mutate],
        );

        if (options.cache === false || !state) {
            return {
                mutate: handleMutate,
                data,
                isPending: isPending(status),
                error,
                reset: resetWrapper,
            };
        }

        return {
            mutate: handleMutate,
            data: state.data as Returned,
            isPending: isPending(state.status),
            error: state.error,
            reset: resetWrapper,
        };
    };

function selectSlice<Returned, PayloadArg>(
    options: CreateMutationOptions<Returned, PayloadArg>,
    arg?: PayloadArg,
): MutationState<Returned> {
    const mutationCache = getQueryClient().getMutationCache();

    const mutation: Mutation<Returned> | undefined = mutationCache.find({
        mutationKey: getMutationKey(options, arg),
    });

    const state = mutation?.state;

    return { data: state?.data, isPending: isPending(state?.status), error: state?.error || null };
}

/**
 * factory function used to generate custom hooks that encapsulate mutation behavior
 * @param options - An object with the following properties:
 * @param options.payloadCreator - A callback function that takes an optional payload argument and returns a Promise resolving to the server's response.
 * @param options.name - A string identifier for the mutation, used in constructing unique mutation keys. It's recommended to use format of domain-name/resource-name/{create,update,delete}.
 * @param [options.cache=false] - A boolean indicating whether caching is enabled for this mutation. When set to false, the mutation will bypass any internal caching mechanism (default: true). It's recommended to set this to false for most use cases.
 *
 * @returns A tuple containing a custom hook (useMutation) and an object with imperative helpers (selectData, selectSlice, mutate). The useLunaMutation hook returns a state object ({ isPending, error, data, mutate, reset }) while the imperative helpers provide access to the current mutation state ({ selectData, selectSlice, mutate }) and allow mutations to be initiated programmatically.
 *
 *  @example
 *  import React from 'react';
 *  import { createMutation, request } from '@luna/api';
 *  import { t } from '@luna/i18n';
 *  import { logger } from '@luna/logger';
 *
 *  interface Payload {
 *      checked: boolean;
 *      guid: string;
 *  }
 *
 *  interface Response {
 *      id: string
 *  }
 *
 *  const [useUpdateConsentsMutation, helpers] = createMutation({
 *      payloadCreator: async ({ checked, guid }: Payload) => {
 *          return request.put<Response>({ checked, guid })
 *      },
 *      name: 'financial-health/consents/update',
 *      cache: false
 *  });
 *
 *  export const UpdateConsents: React.FC = () => {
 *      const { mutate } = useUpdateConsentsMutation();
 *      const { displaySuccess, displayError } = useNotifications('update-consents');
 *
 *      const handleMutate = async () => {
 *          try {
 *              mutate({ checked: true, guid: '123' })
 *              displaySuccess(t('updateConsentsSuccessMessage'))
 *          } catch(error) {
 *              logger.error(error)
 *              displayError(t('updateConsentsErrorMessage'))
 *          }
 *      }
 *
 *      return (
 *          <button onClick={handleMutate}>
 *              Save
 *          </button>
 *      );
 *  };
 * */

export const createMutation = <Returned, PayloadArg = undefined>(
    options: CreateMutationOptions<Returned, PayloadArg>,
): CreateMutationReturn<Returned, PayloadArg> => {
    const useLunaMutation = createUseMutation(options);
    const mutate = (arg?: PayloadArg, hookOptions?: UseMutationOptions): Promise<Returned> => {
        return new Promise((resolve, reject) => {
            const root = createRoot(document.createDocumentFragment());

            root.render(
                createElement(
                    QueryClientProvider,
                    { client: getQueryClient() },
                    createElement(() => {
                        const mutationState = useLunaMutation(arg, hookOptions);

                        useState(() => {
                            mutationState
                                .mutate()
                                .then(resolve)
                                .catch(reject)
                                .finally(() => root.unmount());
                        });
                        return null;
                    }),
                ),
            );
        });
    };
    const selectData = (arg?: PayloadArg): Returned | undefined => {
        return selectSlice(options, arg).data;
    };
    const selectSliceWrapper = (arg?: PayloadArg): MutationState<Returned> => {
        return selectSlice(options, arg);
    };

    return [
        useLunaMutation,
        {
            selectData,
            selectSlice: selectSliceWrapper,
            mutate,
        },
    ];
};
